/**
 * Editing a bar's slots: the operations behind the bar editor (lengths,
 * splitting, delete) and "pull the next bar in", shared by both editors so
 * they behave the same.
 *
 * Bars are solid: an operation changes the slots of one bar (and, for a
 * pull-in, removes the next one) and never moves the bars after it. The
 * rules, with the canvas they come from in `docs/plans/split-bars-beats.md`:
 *
 * - Shorten: the freed beats go to the next slot in the bar; when the slot
 *   is the bar's last, a new rest is appended to take them.
 * - Lengthen: the beats come from the next slot, which keeps at least the
 *   meter's minimum; the bar's last slot cannot grow.
 * - Whole bar: only a slot alone in its bar.
 * - Split: a slot cut at a position inside it keeps the part before it
 *   (and its id, lyrics and shapes); a new empty rest takes the part after.
 * - Delete inside a bar: the slot's beats go to the slot before it (the one
 *   after it when it was first); a slot alone in its bar takes the bar with it.
 * - Pull in: when the next bar holds a single slot, it joins this bar as the
 *   last slot, replacing a trailing empty rest (otherwise the previous last
 *   slot keeps its length, minus the newcomer's minimum).
 * - Every operation settles the bar afterwards: every slot but the last
 *   stores its length, the last stores nothing.
 *
 * Lengths are in the meter's denominator units (`barBeats.ts`).
 */
import { getBarUnits, getMinSlotBeats, getSlotBeats, layoutBarSlots } from './barBeats.js';
import { createBar, defaultSchemaIdGenerator, getRowSlots, removeSlotFromRow } from './barUtils.js';
/** The slot with `beats` set, or with the key removed when null. */
export function withSlotBeats(slot, beats) {
    if (beats === null) {
        if (!('beats' in slot)) {
            return slot;
        }
        const { beats: _beats, ...rest } = slot;
        return rest;
    }
    return { ...slot, beats };
}
/** True for a slot with nothing in it: no chord, no custom text, no lyrics. */
export const isEmptyRest = (slot) => !slot.chord && !slot.customText && !slot.lyrics?.trim();
const sameMeter = (a, b) => a.numerator === b.numerator && a.denominator === b.denominator;
/** A length the picker may offer in this meter: a multiple of the minimum, at least the minimum. */
export function isAllowedSlotBeats(beats, timeSignature) {
    const min = getMinSlotBeats(timeSignature);
    return beats >= min && Number.isInteger(beats / min);
}
/** The bar with its lengths made explicit: every slot but the last stores its length, the last stores nothing. */
export function settleBar(bar, timeSignature) {
    const layout = layoutBarSlots(bar, timeSignature);
    return { ...bar, slots: bar.slots.map((slot, i) => withSlotBeats(slot, layout[i].isLast ? null : layout[i].lengthUnits)) };
}
/**
 * Why a bar isn't what the editor would write, for tests and validators:
 * empty when every slot but the last stores an allowed length, the last
 * stores none, and the lengths fit the bar.
 */
export function checkBarBeats(bar, timeSignature) {
    const problems = [];
    const lastIndex = bar.slots.length - 1;
    let used = 0;
    bar.slots.forEach((slot, i) => {
        const beats = getSlotBeats(slot);
        if (i === lastIndex) {
            if (beats !== null) {
                problems.push(`last slot ${slot.id} stores beats`);
            }
            return;
        }
        if (beats === null) {
            problems.push(`slot ${slot.id} stores no beats`);
        }
        else if (!isAllowedSlotBeats(beats, timeSignature)) {
            problems.push(`slot ${slot.id} stores ${beats}, not allowed in ${timeSignature.numerator}/${timeSignature.denominator}`);
        }
        else {
            used += beats;
        }
    });
    if (lastIndex > 0 && used > getBarUnits(timeSignature) - getMinSlotBeats(timeSignature)) {
        problems.push(`slots use ${used} of ${getBarUnits(timeSignature)} units, leaving the last slot too little`);
    }
    return problems;
}
function locate(row, slotId) {
    for (let barIndex = 0; barIndex < row.bars.length; barIndex++) {
        const bar = row.bars[barIndex];
        const slotIndex = bar.slots.findIndex(slot => slot.id === slotId);
        if (slotIndex !== -1) {
            return { barIndex, bar, slotIndex };
        }
    }
    return null;
}
const replaceBar = (row, barIndex, bar) => {
    const bars = [...row.bars];
    bars[barIndex] = bar;
    return { ...row, bars };
};
/**
 * The row with one slot set to `beats` units (`null` = the whole bar, only
 * for a slot alone in its bar). Unchanged when the value isn't allowed, the
 * slot is the bar's last and would grow, or the next slot can't give that
 * much.
 */
export function setSlotBeats(row, slotId, beats, timeSignature, generateId = defaultSchemaIdGenerator) {
    const unchanged = { row, insertedRestId: null, gapUnits: 0 };
    const place = locate(row, slotId);
    if (!place) {
        return unchanged;
    }
    const { bar, barIndex, slotIndex } = place;
    const lastIndex = bar.slots.length - 1;
    const isLast = slotIndex === lastIndex;
    const units = getBarUnits(timeSignature);
    if (beats === null || (beats === units && bar.slots.length === 1)) {
        if (bar.slots.length !== 1) {
            return unchanged;
        }
        const slot = withSlotBeats(bar.slots[0], null);
        return slot === bar.slots[0] ? unchanged : { ...unchanged, row: replaceBar(row, barIndex, { ...bar, slots: [slot] }) };
    }
    if (!isAllowedSlotBeats(beats, timeSignature) || beats >= units) {
        return unchanged;
    }
    const layout = layoutBarSlots(bar, timeSignature);
    const delta = beats - layout[slotIndex].lengthUnits;
    if (delta === 0) {
        return unchanged;
    }
    const slots = [...bar.slots];
    slots[slotIndex] = withSlotBeats(slots[slotIndex], beats);
    if (delta < 0) {
        if (isLast) {
            const rest = { id: generateId('slot'), chord: null, customText: null, lyrics: null };
            slots.push(rest);
            return { row: replaceBar(row, barIndex, settleBar({ ...bar, slots }, timeSignature)), insertedRestId: rest.id, gapUnits: -delta };
        }
        const next = slotIndex + 1;
        if (next !== lastIndex) {
            slots[next] = withSlotBeats(slots[next], layout[next].lengthUnits - delta);
        }
        return { ...unchanged, row: replaceBar(row, barIndex, settleBar({ ...bar, slots }, timeSignature)) };
    }
    if (isLast) {
        return unchanged;
    }
    const next = slotIndex + 1;
    const nextLength = layout[next].lengthUnits - delta;
    if (nextLength < getMinSlotBeats(timeSignature)) {
        return unchanged;
    }
    if (next !== lastIndex) {
        slots[next] = withSlotBeats(slots[next], nextLength);
    }
    return { ...unchanged, row: replaceBar(row, barIndex, settleBar({ ...bar, slots }, timeSignature)) };
}
/**
 * The row with a slot cut in two at `atUnits` (from the start of its bar):
 * the slot keeps one part, a new empty rest next to it takes the other —
 * the part after the cut by default, the part before with `restSide:
 * 'before'` (the slot, with its id, lyrics and shapes, then starts at the
 * cut). Unchanged unless both parts are allowed lengths (at least the
 * meter's minimum, halves only where the meter allows them).
 */
export function splitSlotAt(row, slotId, atUnits, timeSignature, generateId = defaultSchemaIdGenerator, restSide = 'after') {
    const place = locate(row, slotId);
    if (!place) {
        return { row, insertedRestId: null };
    }
    const { bar, barIndex, slotIndex } = place;
    const { startUnits, lengthUnits } = layoutBarSlots(bar, timeSignature)[slotIndex];
    const before = atUnits - startUnits;
    const after = lengthUnits - before;
    if (!isAllowedSlotBeats(before, timeSignature) || !isAllowedSlotBeats(after, timeSignature)) {
        return { row, insertedRestId: null };
    }
    const rest = { id: generateId('slot'), chord: null, customText: null, lyrics: null };
    const slots = [...bar.slots];
    const parts = restSide === 'before'
        ? [withSlotBeats(rest, before), withSlotBeats(bar.slots[slotIndex], after)]
        : [withSlotBeats(bar.slots[slotIndex], before), withSlotBeats(rest, after)];
    slots.splice(slotIndex, 1, ...parts);
    return { row: replaceBar(row, barIndex, settleBar({ ...bar, slots }, timeSignature)), insertedRestId: rest.id };
}
/** The row with the bar of that id replaced (the bar editor's Apply); unchanged when no bar has the id. */
export function replaceBarInRow(row, barId, bar) {
    const barIndex = row.bars.findIndex(b => b.id === barId);
    return barIndex === -1 ? row : replaceBar(row, barIndex, bar);
}
/**
 * The row without the slot. Inside a split bar its beats go to the slot
 * before it (the one after it when it was first) and the bar stays whole; a
 * slot alone in its bar takes the bar with it (`removeSlotFromRow`).
 */
export function removeSlotFromBar(row, slotId, timeSignature) {
    const place = locate(row, slotId);
    if (!place) {
        return row;
    }
    const { bar, barIndex, slotIndex } = place;
    if (bar.slots.length === 1) {
        return removeSlotFromRow(row, slotId);
    }
    const layout = layoutBarSlots(bar, timeSignature);
    const removed = layout[slotIndex].lengthUnits;
    const slots = bar.slots.filter((_, i) => i !== slotIndex);
    const newLastIndex = slots.length - 1;
    if (slotIndex > 0) {
        const prev = slotIndex - 1;
        if (prev < newLastIndex) {
            slots[prev] = withSlotBeats(slots[prev], layout[prev].lengthUnits + removed);
        }
    }
    else if (newLastIndex > 0) {
        slots[0] = withSlotBeats(slots[0], layout[1].lengthUnits + removed);
    }
    return replaceBar(row, barIndex, settleBar({ ...bar, slots }, timeSignature));
}
/** Where a whole bar may be added next to this slot: only at its bar's edges. */
export function canInsertBeside(row, slotId) {
    const place = locate(row, slotId);
    if (!place) {
        return { before: false, after: false };
    }
    return { before: place.slotIndex === 0, after: place.slotIndex === place.bar.slots.length - 1 };
}
/** The row with every slot in a bar of its own and no beats stored: what a time-signature change leaves. */
export function clearBeats(row, generateId) {
    if (row.bars.every(bar => bar.slots.length === 1 && getSlotBeats(bar.slots[0]) === null && !('beats' in bar.slots[0]))) {
        return row;
    }
    return { ...row, bars: getRowSlots(row).map(slot => createBar(withSlotBeats(slot, null), generateId)) };
}
export function clearBeatsInSections(sections, generateId) {
    return sections.map(section => ({ ...section, rows: section.rows.map(row => clearBeats(row, generateId)) }));
}
/**
 * `next`'s bars appended to `prev` (a row merge). Beats sized for another
 * meter don't carry over: when the rows' meters differ, the moved bars are
 * cleared first.
 */
export function mergeRowInto(prev, next, prevMeter, nextMeter, generateId) {
    const moved = sameMeter(prevMeter, nextMeter) ? next : clearBeats(next, generateId);
    return { ...prev, bars: [...prev.bars, ...moved.bars] };
}
